import React, { useState, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Backdrop from "@material-ui/core/Backdrop";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionActions from "@material-ui/core/AccordionActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import logger from "../logger";
import { generateTimestampId } from "../functions";
import { updateStore } from "../graphql/mutations";
import { AppContext } from "../views/App";

const drawerWidth = "100vw";

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: "#fafafa"
  },
  drawerContainer: {
    overflow: "auto"
  },
  backButton: {
    marginRight: theme.spacing(2)
  },
  addButton: {
    marginLeft: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  container: {
    marginTop: theme.spacing(1)
  },
  column: {
    flexBasis: "50%"
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  dialog: {
    transform: "translateY(-100%)"
  }
}));

export const updateCategorias = async (action, categoria, store, onStart, onEnd, onComplete) => {
  onStart();

  try {
    let categories = [];

    if (action === "create") {
      categories = [...store.body.categories, categoria];
    }

    if (action === "update") {
      for (let i = 0; i < store.body.categories.length; i++) {
        const item = store.body.categories[i];

        const category = {
          value: item.value,
          label: item.label
        };

        if (item.value === categoria.value) {
          category.label = categoria.label;
        }

        categories.push(category);
      }
    }

    if (categories.length > 0) {
      categories.sort((a, b) => {
        if (a.label > b.label) {
          return 1;
        }
        if (a.label < b.label) {
          return -1;
        }
        return 0;
      });

      const vars = {
        input: {
          id: store.id,
          body: JSON.stringify({
            ...store.body,
            categories
          })
        }
      };

      const result = await API.graphql(graphqlOperation(updateStore, vars));

      if (result.data.updateStore) {
        const body = JSON.parse(result.data.updateStore.body);

        onComplete({
          ...result.data.updateStore,
          body
        });
      }
    }
  } catch (err) {
    logger("CategoriasList.updateCategorias.error", err);
  } finally {
    onEnd();
  }
};

const CategoriasList = ({ open, onClose }) => {
  const classes = useStyles();
  const { store, onStoreChange } = useContext(AppContext);
  const [enviando, setEnviando] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [editLabel, setEditLabel] = useState("");

  const expandPanel = (panel, category) => (_, isExpanded) => {
    setEditLabel(isExpanded && category ? category.label : "");
    setExpanded(isExpanded ? panel : false);
  };

  const openAddDialog = () => {
    setOpenAdd(true);
  };

  const closeAddDialog = () => {
    setOpenAdd(false);
  };

  const validar = value => {
    return value.trim() !== "";
  };

  const canAdd = () => {
    return validar(addLabel);
  };

  const canUpdate = () => {
    return validar(editLabel);
  };

  const guardar = (action, category) => {
    updateCategorias(
      action,
      category,
      store,
      () => {
        closeAddDialog();
        setAddLabel("");
        setEnviando(true);
      },
      () => {
        setEnviando(false);
      },
      store => {
        setExpanded(false);
        setEditLabel("");
        onStoreChange(store);
      }
    );
  };

  const agregar = () => {
    if (canAdd()) {
      guardar("create", {
        label: addLabel.trim(),
        value: generateTimestampId()
      });
    }
  };

  const actualizar = category => {
    if (canUpdate()) {
      guardar("update", {
        label: editLabel.trim(),
        value: category.value
      });
    }
  };

  const renderAddDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialog }}
        open={openAdd}
        onClose={closeAddDialog}
      >
        <DialogTitle>Agregar categoría</DialogTitle>

        <DialogContent>
          <DialogContentText>Ingrese el nombre de la categoría.</DialogContentText>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            color="primary"
            value={addLabel}
            onChange={({ target }) => setAddLabel(target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeAddDialog} color="primary">
            Cancelar
          </Button>
          <Button disabled={!canAdd()} onClick={agregar} color="primary">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Drawer
      className={classes.drawer}
      open={open}
      anchor="right"
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <Backdrop className="backdrop" open={enviando}>
        <CircularProgress />
      </Backdrop>

      {renderAddDialog()}

      <AppBar position="sticky">
        <Toolbar>
          <IconButton className={classes.backButton} edge="start" color="inherit" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>

          <Typography className={classes.title} variant="h6">
            Categorías
          </Typography>

          <IconButton
            className={classes.searchButton}
            edge="end"
            color="inherit"
            onClick={openAddDialog}
          >
            <AddIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container className={classes.container} disableGutters maxWidth="sm">
        {store.body.categories.map((category, i) => (
          <Accordion
            key={`expansion-${i}`}
            expanded={expanded === `category-${i}`}
            onChange={expandPanel(`category-${i}`, category)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {expanded !== `category-${i}` && (
                <>
                  <div className={classes.column}>
                    <Typography className={classes.heading}>{category.label}</Typography>
                  </div>
                </>
              )}
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <TextField
                  fullWidth
                  color="primary"
                  variant="standard"
                  value={editLabel}
                  onChange={({ target }) => setEditLabel(target.value)}
                />
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null, null)}>
                Cancelar
              </Button>
              <Button
                disabled={!canUpdate()}
                size="small"
                color="primary"
                onClick={() => actualizar(category)}
              >
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>
        ))}
      </Container>
    </Drawer>
  );
};

export default CategoriasList;
